import React from "react"
import { Formik, FormikHelpers } from "formik"
import * as yup from "yup"
import {
    Box, Stack, Grid, TextInput, Title,
    Group, Button, NativeSelect, Loader, Select,
    Card,
    Avatar,
    Paper,
    Text,
    Badge
} from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { Icon } from "@iconify/react/dist/iconify.js"

import { useAppState, useActions } from "src/state"
import { UserFragment } from "src/state/gql/_types"
import { Icons } from "src/components/ui/Icons"
import { usTimezones } from "src/utils/dates"
import { changeEmptyPropsDeep } from "src/utils/objects"
import { statesList } from "src/utils/forms"

type ProfileFormValues = {
    first_name: string
    last_name: string
    phone: string
    timezone: string
    address_line1: string
    address_line2: string
    city: string
    state: string
    postal_code: string
    country: string
}

const validationSchema = yup.object().shape({
    first_name: yup.string().nullable(),
    last_name: yup.string().nullable(),
    phone: yup.string().nullable(),
    timezone: yup.string().nullable(),
    address_line1: yup.string().nullable(),
    address_line2: yup.string().nullable(),
    city: yup.string().nullable(),
    state: yup.string().nullable(),
    postal_code: yup.string().nullable(),
    country: yup.string().nullable(),
})

const extractFormValues = (profile: UserFragment): ProfileFormValues => ({
    first_name: profile.first_name || "",
    last_name: profile.last_name || "",
    phone: profile.phone || "",
    timezone: profile.timezone || "",
    address_line1: profile.address_line1 || "",
    address_line2: profile.address_line2 || "",
    city: profile.city || "",
    state: profile.state || "",
    postal_code: profile.postal_code || "",
    country: profile.country || "",
})

const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 10)
    if (digits.length === 0) return ""
    if (digits.length <= 3) return `(${digits}`
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const unformatPhone = (value: string): string => value.replace(/\D/g, "").slice(0, 10)

const timezoneOptions = [...usTimezones]

export const EditableUserProfile = () => {

    const profile = useAppState(state => state.profile)
    const { updateProfile } = useActions()

    if (!profile) return <Loader />

    const initialValues = extractFormValues(profile)

    const onSubmit = async (
        values: ProfileFormValues,
        { setSubmitting, resetForm }: FormikHelpers<ProfileFormValues>
    ) => {
        const payload = changeEmptyPropsDeep(values)
        const success = await updateProfile(payload)

        if (success) {
            notifications.show({
                icon: <Icon icon={Icons.warningCircle} width={24} />,
                color: "green",
                autoClose: 4000,
                title: "Success!",
                message: "Profile Updated",
            })
            resetForm({ values })
        } else {
            notifications.show({
                icon: <Icon icon={Icons.warningCircle} width={24} />,
                color: "red",
                autoClose: 4000,
                title: "Oops!",
                message: "Could Not Update Profile",
            })
        }
        setSubmitting(false)
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnMount={false}
            validateOnChange={true}
            enableReinitialize={true}
        >
            {({ values, errors, handleBlur, handleSubmit, submitForm, isSubmitting, isValid, dirty, setFieldValue, resetForm }) => (
                <Box component="form" onSubmit={handleSubmit}>
                    <Grid gutter="lg">

                        <Grid.Col span={{ base: 12 }}>
                            <Card padding="lg" radius="md">
                                <Group justify="space-between" align="flex-start" wrap="wrap">
                                    <Group align="center" gap="md">
                                        <Avatar
                                            name={`${values.first_name} ${values.last_name}`}
                                            size={64}
                                            radius="xl"
                                            color="initials"
                                        />
                                        <Stack gap={2}>
                                            <Group gap="xs" align="center">
                                                <Title order={3}>
                                                    {`${values.first_name || ""} ${values.last_name || ""}`.trim() || "Unnamed Talent"}
                                                </Title>
                                            </Group>
                                            <Text size="sm" c="dimmed">
                                                {profile.email || "No email on file"}
                                            </Text>
                                        </Stack>
                                    </Group>
                                    <Group gap="xs" mt={{ base: "xs", sm: 0 }}>
                                        {(values.city || values.state) && (
                                            <Badge variant="light" leftSection={<Icon icon={Icons.location} width={14} />}>
                                                {[values.city, values.state].filter(Boolean).join(", ")}
                                            </Badge>
                                        )}
                                    </Group>
                                </Group>
                            </Card>
                        </Grid.Col>

                        <Grid.Col span={{ base: 12, sm: 6 }}>

                            <Stack gap="lg">

                                {/* Personal Information */}

                                <Paper p="md" radius="md" bg="gray.3">
                                    <Group gap="xs" align="center">
                                        <Icon icon={Icons.user} width={18} />
                                        <Title order={5}>Personal Information</Title>
                                    </Group>
                                </Paper>

                                <Grid gutter="md" px="sm">
                                    <Grid.Col span={{ base: 12 }}>
                                        <TextInput label="Email" value={profile.email || ""} disabled />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                        <TextInput
                                            label="First Name"
                                            name="first_name"
                                            value={values.first_name}
                                            onChange={(e) => setFieldValue("first_name", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.first_name}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                        <TextInput
                                            label="Last Name"
                                            name="last_name"
                                            value={values.last_name}
                                            onChange={(e) => setFieldValue("last_name", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.last_name}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                        <TextInput
                                            label="Phone"
                                            name="phone"
                                            value={formatPhone(values.phone)}
                                            onChange={(e) => setFieldValue("phone", unformatPhone(e.target.value))}
                                            onBlur={handleBlur}
                                            error={errors.phone}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                        <NativeSelect
                                            label="Timezone"
                                            name="timezone"
                                            data={[
                                                { label: "Select Timezone...", value: "" },
                                                ...timezoneOptions
                                            ]}
                                            value={values.timezone}
                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                setFieldValue("timezone", e.target.value)
                                            }}
                                            onBlur={handleBlur}
                                            error={errors.timezone}
                                        />
                                    </Grid.Col>
                                </Grid>

                            </Stack>

                        </Grid.Col>

                        <Grid.Col span={{ base: 12, sm: 6 }}>

                            <Stack gap="lg">

                                {/* Address */}

                                <Paper p="md" radius="md" bg="gray.3">
                                    <Group gap="xs" align="center">
                                        <Icon icon="ph:map-pin-line-duotone" width={18} />
                                        <Title order={5}>Address</Title>
                                    </Group>
                                </Paper>

                                <Grid gutter="md" px="sm">
                                    <Grid.Col span={12}>
                                        <TextInput
                                            label="Address Line 1"
                                            name="address_line1"
                                            value={values.address_line1}
                                            onChange={(e) => setFieldValue("address_line1", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.address_line1}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={12}>
                                        <TextInput
                                            label="Address Line 2"
                                            name="address_line2"
                                            value={values.address_line2}
                                            onChange={(e) => setFieldValue("address_line2", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.address_line2}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <TextInput
                                            label="City"
                                            name="city"
                                            value={values.city}
                                            onChange={(e) => setFieldValue("city", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.city}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <Select
                                            label="State"
                                            name="state"
                                            data={statesList}
                                            value={values.state}
                                            onChange={(value) => setFieldValue("state", value || "")}
                                            onBlur={handleBlur}
                                            error={errors.state}
                                            searchable
                                            clearable
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <TextInput
                                            label="Postal Code"
                                            name="postal_code"
                                            value={values.postal_code}
                                            onChange={(e) => setFieldValue("postal_code", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.postal_code}
                                        />
                                    </Grid.Col>
                                </Grid>

                            </Stack>

                        </Grid.Col>

                        <Grid.Col span={{ base: 12 }}>

                            {/* Save / Cancel */}
                            <Group justify="flex-end" pb="xl">
                                <Button
                                    variant="default"
                                    onClick={() => resetForm()}
                                    disabled={!dirty}
                                    size="lg"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="green"
                                    onClick={submitForm}
                                    loading={isSubmitting}
                                    disabled={!dirty || !isValid}
                                    size="lg"
                                >
                                    Save
                                </Button>
                            </Group>

                        </Grid.Col>

                    </Grid>
                </Box>
            )}
        </Formik>
    )
}
